import { useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useBodyOverflowLock } from '@/hooks/useBodyOverflowLock';
import { useBreakpoint } from '@/hooks/useIsMobile';
import { useSwipePages } from '@/hooks/useSwipePages';
import { ViewerHeader } from './ViewerHeader';
import { ViewerToolbar } from './ViewerToolbar';
import { ViewerFooter } from './ViewerFooter';
import { ViewerSidebar } from './ViewerSidebar';
import { PageNavBar } from './PageNavBar';
import { MiniPlayer } from './MiniPlayer';
import { SwipeOverlay } from './SwipeOverlay';
import type { FileType } from '@/types/library';
import type { FavoriteCachedData, FavoriteItem } from '@/types/favorites';

function handleNavigationKey(
  key: string,
  currentPage: number | undefined,
  totalPages: number | undefined,
  onPageChange: ((page: number) => void) | undefined
): void {
  if (key === 'ArrowLeft' && onPageChange && currentPage != null && currentPage > 1) {
    onPageChange(currentPage - 1);
  } else if (
    key === 'ArrowRight' &&
    onPageChange &&
    currentPage != null &&
    totalPages != null &&
    currentPage < totalPages
  ) {
    onPageChange(currentPage + 1);
  }
}

function handleScaleKey(
  key: string,
  scale: number | undefined,
  onScaleChange: ((scale: number) => void) | undefined
): void {
  if (!onScaleChange || scale == null) return;
  if (key === '+' || key === '=') onScaleChange(Math.min(3, scale + 0.25));
  else if (key === '-') onScaleChange(Math.max(0.5, scale - 0.25));
}

export interface ViewerShellProps {
  title?: string;
  subtitle?: string;
  type: FileType;
  fileId?: string;
  onClose: () => void;
  onDownload?: () => void;
  currentPage?: number;
  totalPages?: number;
  onPageChange?: (page: number) => void;
  scale?: number;
  onScaleChange?: (scale: number) => void;
  onRotate?: () => void;
  fileSize?: number;
  tags?: string[];
  mimeType?: string;
  thumbnailUrl?: string;
  favoriteCachedData?: FavoriteCachedData;
  /** Called when user selects a favorited material from the favorites list */
  onOpenFavorite?: (favorite: FavoriteItem) => void;
  /** Disable swipe navigation (e.g. when zoomed) */
  swipeEnabled?: boolean;
  /** Keyboard hint for desktop footer */
  keyboardHint?: string;
  children: React.ReactNode;
}

export function ViewerShell({
  title,
  subtitle,
  type,
  fileId,
  onClose,
  onDownload,
  currentPage,
  totalPages,
  onPageChange,
  scale,
  onScaleChange,
  onRotate,
  fileSize,
  tags,
  thumbnailUrl,
  favoriteCachedData,
  onOpenFavorite,
  swipeEnabled = true,
  keyboardHint,
  children,
}: ViewerShellProps) {
  const isMobile = useBreakpoint(1024);

  useBodyOverflowLock(true);

  const hasPages = currentPage != null && totalPages != null && totalPages > 1 && onPageChange;

  const {
    handlers: swipeHandlers,
    swipeOffset,
    isSwiping,
  } = useSwipePages({
    currentPage: currentPage ?? 1,
    totalPages: totalPages ?? 1,
    onPageChange: onPageChange ?? (() => {}),
    enabled: swipeEnabled && isMobile && !!hasPages,
  });

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;

      const active = document.activeElement;
      const insidePlayer = active?.closest('.plyr') != null;

      if (e.key === 'Escape') {
        onClose();
        return;
      }

      if (insidePlayer && (e.key === 'ArrowLeft' || e.key === 'ArrowRight' || e.key === ' '))
        return;

      if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
        handleNavigationKey(e.key, currentPage, totalPages, onPageChange);
        return;
      }

      if (e.key === '+' || e.key === '=' || e.key === '-') {
        handleScaleKey(e.key, scale, onScaleChange);
        return;
      }

      if (e.key === 'r' || e.key === 'R') {
        onRotate?.();
      }
    },
    [onClose, onPageChange, currentPage, totalPages, onScaleChange, scale, onRotate]
  );

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  // Browser back-button support
  useEffect(() => {
    history.pushState({ viewerOpen: true }, '');
    const handlePopState = () => onClose();
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [onClose]);

  const modal = (
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 z-[100] flex flex-col bg-[#faf8f5]"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.2 }}
      >
        {isMobile ? (
          /* ===== MOBILE LAYOUT (Design 11a) ===== */
          <>
            <ViewerHeader
              title={title}
              subtitle={subtitle}
              onClose={onClose}
              onDownload={onDownload}
              onOpenFavorite={onOpenFavorite}
            />

            <ViewerToolbar
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={onPageChange}
              scale={scale}
              onScaleChange={onScaleChange}
              onRotate={onRotate}
              fileId={fileId}
              favoriteCachedData={favoriteCachedData}
            />

            {/* Content area with swipe */}
            <div className="flex-1 relative overflow-hidden" {...swipeHandlers}>
              <div
                className="w-full h-full overflow-auto"
                style={{
                  transform: isSwiping ? `translateX(${swipeOffset}px)` : undefined,
                  transition: isSwiping ? 'none' : 'transform 0.2s ease-out',
                }}
              >
                {children}
              </div>

              {hasPages && (
                <SwipeOverlay
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={onPageChange}
                />
              )}
            </div>

            {hasPages && (
              <PageNavBar
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={onPageChange}
              />
            )}

            <MiniPlayer />
          </>
        ) : (
          /* ===== DESKTOP LAYOUT (Design 11b) ===== */
          <>
            <ViewerHeader
              title={title}
              subtitle={subtitle}
              onClose={onClose}
              onDownload={onDownload}
              onOpenFavorite={onOpenFavorite}
            />

            <div className="flex-1 flex overflow-hidden">
              {/* Left: toolbar + content */}
              <div className="flex-1 flex flex-col min-w-0">
                <ViewerToolbar
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={onPageChange}
                  scale={scale}
                  onScaleChange={onScaleChange}
                  onRotate={onRotate}
                  fileId={fileId}
                  favoriteCachedData={favoriteCachedData}
                />

                <div className="flex-1 relative overflow-hidden">
                  <div className="w-full h-full overflow-auto">{children}</div>

                  {hasPages && (
                    <SwipeOverlay
                      currentPage={currentPage}
                      totalPages={totalPages}
                      onPageChange={onPageChange}
                      showHint={false}
                    />
                  )}
                </div>
              </div>

              {/* Right: sidebar */}
              <ViewerSidebar
                type={type}
                title={title}
                fileId={fileId}
                favoriteCachedData={favoriteCachedData}
                onDownload={onDownload}
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={onPageChange}
                tags={tags}
                fileSize={fileSize}
                thumbnailUrl={thumbnailUrl}
              />
            </div>

            <ViewerFooter
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={onPageChange}
              hintText={keyboardHint}
            />
          </>
        )}
      </motion.div>
    </AnimatePresence>
  );

  return createPortal(modal, document.body);
}
